import { Grid, Container, Typography, Stack, Box, Divider, useMediaQuery } from '@material-ui/core';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import _ from 'lodash';
import { set } from '../store/slices/identitySlice';
import { useIsFeatureFlagEnabled } from '../utils/FeatureFlags';
import Page from '../components/Page';
import DismissableAlert from '../components/common/DismissibleAlert';
import { secondsToDateStr } from '../utils/formatTime';
import { DISPLAY_TIME_TO_DELETION_DURATION, PAST_DUE_DELETION_DATE } from '../utils/constants';

import {
  StatusTimeline,
  GeneralInfo,
  RackLifecycleInfo,
  LRUInfo,
  VMInfo,
  LoadingInfo,
  Connectivity,
  RackNotFound,
  DeleteRackButton,
  ConnectRackButton,
  SetRackStateButton,
  RackTDsButton,
  SnapshotButton,
  GatewayInfo,
  RackActionsMenu
} from '../components/rack/details';
import { getIDToken } from '../components/authentication/login/amplify';

const ACTIVE_JOB_STATUSES = ['inprogress', 'cancelling'];

// checks to see if any LRUs are enabled in the rack
function anyLRUsEnabled(rack) {
  if (rack.lrus) {
    return rack.lrus.length > 0;
  }
  return false;
}

// checks to see if any VMs are enabled in the rack
function anyVMsEnabled(rack) {
  if (rack.generic_vms) {
    return rack.generic_vms.length > 0;
  }
  return false;
}

function addCompletedStatus(rack) {
  if (rack.statuses.length > 0) {
    const lastStatus = rack.statuses[rack.statuses.length - 1];

    if (lastStatus.metadata.status !== 'completed') {
      // Add a final status to rack indicating deletion is completed
      const rackCopy = { ...rack };
      rackCopy.statuses.push({
        id: '',
        metadata: { error: '', notes: '', rack: '', rack_status: { details: '', status: '' } },
        status: 'completed'
      });
      return rackCopy;
    }
  }

  return rack;
}

function isJobInProgress(rack) {
  if (rack.statuses?.length > 0) {
    return ACTIVE_JOB_STATUSES.includes(rack.statuses[rack.statuses.length - 1].status);
  }
  return false;
}

// const representing the substring returned in errors when a rack does not exist
const RACK_NOT_FOUND_SUBSTR = 'no entry found in database';

export default function RackDetails() {
  const navigate = useNavigate();
  const { rackname } = useParams();
  const [rack, setRack] = useState({
    running: { state: 'stopped' },
    statuses: [],
    lifecycle: { inactivity_policy: {} }
  });
  const dispatch = useDispatch();
  const isTDsButtonEnabled = useIsFeatureFlagEnabled('flight_tds');
  const jobInProgress = isJobInProgress(rack);
  const [deletionDate, setDeletionDate] = useState('');

  // Flip to the Actions menu below MUI's md breakpoint. The buttons row stays
  // visible above md; opening the help sidebar on a wide screen no longer
  // triggers the menu swap — `rowGap` on the Stack handles the wrap.
  const isNarrow = useMediaQuery((theme) => theme.breakpoints.down('md'));
  const showActionsMenu = isNarrow;

  function rackExists(rack, error) {
    if (error.response?.data?.message?.includes(RACK_NOT_FOUND_SUBSTR)) {
      if (!rack.name) {
        return false;
      }
      // add completion status and return true
      setRack(addCompletedStatus(rack));
      return true;
    }

    return true;
  }

  const getRackDetails = async (rackname, setRack, navigate) => {
    // call api for get rack details
    const jwt = await getIDToken();
    if (jwt) {
      axios
        .get(`${process.env.REACT_APP_API}/racks/name/${rackname}`, {
          headers: {
            Authorization: `Bearer ${jwt}`
          }
        })
        .then((response) => {
          // update rack if there's new data
          setRack((prevRack) => {
            if (_.isEqual(prevRack, response.data[0])) {
              return prevRack;
            }
            return response.data[0];
          });
          dispatch(set(response.data[0].provisioner_identity));
          determineDeletionDate(response.data[0]);
        })
        .catch((error) => {
          // Check if error is due to the rack not existing and if so, redirect to 404 page
          if (!rackExists(rack, error)) {
            navigate('/404', { replace: true });
            return;
          }
          // Check if error is due to not being authorized to view the page
          if (error.response && error.response.status === 401) {
            navigate('/401', { replace: true });
            return;
          }
          console.error('failed to get rack details', error);
        });
    } else {
      console.error('failed to get JWT, user needs to login');
    }
  };

  const determineDeletionDate = (rack) => {
    const inactivity = rack.lifecycle?.inactivity_policy;
    if (!inactivity) {
      setDeletionDate('');
      return;
    }
    if (inactivity.time_till_delete != null && inactivity.time_till_delete > 0) {
      const deleteDate = secondsToDateStr(inactivity.time_till_delete);
      setDeletionDate(deleteDate);
    } else {
      const deleteDate = secondsToDateStr(0);
      setDeletionDate(deleteDate);
    }
  };

  useEffect(() => {
    getRackDetails(rackname, setRack, navigate);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      getRackDetails(rackname, setRack, navigate);
    }, 5000);
    return () => clearInterval(interval);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [rackname, rack]);

  if (rack) {
    return (
      <Page title={`${rackname} Details`}>
        <Container maxWidth={false}>
          {/* Inactivity deletion banner — shown only when time_till_delete is set (not null)
              and deletion is within the display window
              (time_till_delete < DISPLAY_TIME_TO_DELETION_DURATION, i.e. under ~14 days).
              The ternary picks the message variant by time_till_delete:
                - > 0: deletion is scheduled for a future date, show the computed date
                - <= 0: the deletion deadline has passed, warn that deletion is imminent */}
          {rack.lifecycle?.inactivity_policy?.time_till_delete != null &&
            rack.lifecycle.inactivity_policy.time_till_delete < DISPLAY_TIME_TO_DELETION_DURATION &&
            (rack.lifecycle.inactivity_policy.time_till_delete > 0 ? (
              <Stack marginBottom={2}>
                <DismissableAlert
                  severity="warning"
                  variant="filled"
                  alignItems="center"
                  action={
                    <SetRackStateButton
                      rackDetails={rack}
                      variant="outlined"
                      size="small"
                      label="Keep Active"
                      showIcon={false}
                      tooltipTitle="Keep Virtual Rack active"
                      sx={{ color: 'inherit', borderColor: '#663c00' }}
                    />
                  }
                >
                  <strong>This Virtual Rack will be deleted due to inactivity.</strong>&nbsp;
                  <br />
                  Scheduled for deletion on <strong>{deletionDate}</strong> due to{' '}
                  <strong>{rack.lifecycle?.inactivity_policy?.deletion_policy?.window_days}</strong>{' '}
                  days of inactivity. &nbsp;Press the 'Keep Active' button to prevent deletion.
                </DismissableAlert>
              </Stack>
            ) : (
              // this stack can be deleted after initial Deletion date 09/08/2026
              <Stack marginBottom={2}>
                <DismissableAlert
                  severity="warning"
                  variant="filled"
                  alignItems="center"
                  action={
                    <SetRackStateButton
                      rackDetails={rack}
                      variant="outlined"
                      size="small"
                      label="Keep Active"
                      showIcon={false}
                      tooltipTitle="Keep Virtual Rack active"
                      sx={{
                        color: 'inherit',
                        borderColor: '#663c00'
                      }}
                    />
                  }
                >
                  <strong>This Virtual Rack will be deleted due to inactivity</strong>
                  <br />
                  Scheduled for deletion on {PAST_DUE_DELETION_DATE} due to{' '}
                  <strong>{rack.lifecycle?.inactivity_policy?.deletion_policy?.window_days}</strong>{' '}
                  days of inactivity. &nbsp; Press the 'Keep Active' button to prevent deletion.
                </DismissableAlert>
              </Stack>
            ))}
          <Grid
            container
            data-testid="rack_details"
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            marginBottom={4}
          >
            <Grid item md={12} lg={4}>
              <Box ml={2}>
                <Typography variant="h3">VRack Details</Typography>
                <Divider sx={{ width: 270 }} />
                <Box sx={{ marginTop: 1 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      textTransform: 'uppercase',
                      fontWeight: 100,
                      letterSpacing: 3,
                      opacity: 0.7,
                      marginBottom: 1
                    }}
                  >
                    {rackname}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item md={12} lg={8}>
              <Stack
                direction="row"
                spacing={1}
                flexWrap="wrap"
                justifyContent="flex-end"
                alignItems="center"
                sx={{
                  display: showActionsMenu ? 'none' : 'flex',
                  rowGap: 1
                }}
              >
                <SnapshotButton rackDetails={rack} disabled={jobInProgress} />
                {isTDsButtonEnabled && anyLRUsEnabled(rack) && (
                  <RackTDsButton rackDetails={rack} disabled={jobInProgress} />
                )}
                <ConnectRackButton rackDetails={rack} />
                <SetRackStateButton rackDetails={rack} disabled={isJobInProgress(rack)} />
                <DeleteRackButton
                  rack={rack.name}
                  region={rack.region}
                  disabled={isJobInProgress(rack)}
                />
              </Stack>
            </Grid>
          </Grid>

          {/*
            Below MUI's md breakpoint, swap the row of action buttons for a
            single full-width "Actions" dropdown rendered in its own row above
            the content.
          */}
          {showActionsMenu && (
            <Box sx={{ mb: 3 }}>
              <RackActionsMenu
                rack={rack}
                isTDsButtonEnabled={isTDsButtonEnabled}
                jobInProgress={jobInProgress}
              />
            </Box>
          )}
          <Grid container spacing={3}>
            <Grid container item xs={12} spacing={3}>
              <Grid item xs={12}>
                <StatusTimeline rackDetails={rack} jobInProgress={jobInProgress} />
              </Grid>
              <Grid item xs={12} md={6} lg={8}>
                <GeneralInfo rackDetails={rack} />
              </Grid>
              <Grid item xs={12} md={6} lg={4}>
                <RackLifecycleInfo rackDetails={rack} />
              </Grid>
            </Grid>
            {anyLRUsEnabled(rack) && (
              <Grid item xs={12}>
                <LRUInfo rackDetails={rack} jobInProgress={jobInProgress} />
              </Grid>
            )}

            <Grid item xs={12}>
              <GatewayInfo rackDetails={rack} jobInProgress={jobInProgress} />
            </Grid>

            {anyVMsEnabled(rack) && (
              <Grid item xs={12}>
                <VMInfo rackDetails={rack} jobInProgress={jobInProgress} />
              </Grid>
            )}

            <Grid item xs={12}>
              <Connectivity rackDetails={rack} />
            </Grid>

            {anyLRUsEnabled(rack) && (
              <Grid item xs={12} sm={12}>
                <LoadingInfo rackDetails={rack} jobInProgress={jobInProgress} />
              </Grid>
            )}
          </Grid>
        </Container>
      </Page>
    );
  }
  return <RackNotFound rackname={rackname} />;
}
